import { ReactNode, useRef } from 'react'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import ReactQuill from 'react-quill'

import 'react-quill/dist/quill.snow.css'

import { Box, InputLabel, Stack, SxProps, Theme } from '@mui/material'

import colors from '@/assets/scss/_themes-vars.module.scss'

type QuillControllerProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  label?: ReactNode
  name: FieldPath<TFieldValues>
  readOnly?: boolean
  sx?: SxProps<Theme>
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    ['image', 'link'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ header: [1, 2, 3, 4, 5, 6] }],
    [{ font: [] }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['clean'],
  ],
}

const style = {
  flex: 1,
  '& .quill': {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '.ql-container': {
      minHeight: '80px',
    },
    '.ql-toolbar': {
      '&.ql-snow': {
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
      },
    },
    '.ql-container.ql-snow': {
      borderBottomLeftRadius: '16px',
      borderBottomRightRadius: '16px',
    },
    '.ql-tooltip.ql-editing': {
      left: '0 !important',
    },
  },
}

function QuillController<TFieldValues extends FieldValues>({ control, label, name, readOnly = false, sx }: QuillControllerProps<TFieldValues>) {
  const { field } = useController({ control, name })
  const reactQuillRef = useRef<ReactQuill>(null)

  const handleChange = (value: string) => {
    field.onChange(value)
  }

  const checkCharacterCount = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const unprivilegedEditor = reactQuillRef.current?.unprivilegedEditor
    if (unprivilegedEditor && unprivilegedEditor.getLength() > 250 && event.code !== 'Backspace' && event.code !== 'Space') {
      event.preventDefault()
    }
  }

  return (
    <Stack sx={{ ...style, ...sx }} position="relative">
      {label && (
        <InputLabel
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            transform: 'none',
            color: colors.gray700,
          }}
        >
          {label}
        </InputLabel>
      )}
      {readOnly ? (
        <Box
          className="ql-editor"
          dangerouslySetInnerHTML={{ __html: field.value || '' }}
          sx={{
            color: (theme) => theme.palette.grey[900],
            fontSize: '15px',
            lineHeight: '24px',
            p: '10px',
            border: '1px solid',
            borderRadius: '5px',
            minHeight: '100px',
            marginTop: 3,
            '& img': {
              objectFit: 'contain',
              maxWidth: '100%',
              height: 'auto',
            },
            borderColor: (theme) => theme.palette.grey[300],
          }}
        />
      ) : (
        <ReactQuill
          onKeyDown={checkCharacterCount}
          ref={reactQuillRef}
          value={field.value || ''}
          onChange={handleChange}
          onBlur={field.onBlur}
          modules={modules}
          theme="snow"
        />
      )}
    </Stack>
  )
}

export default QuillController
